import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import type { DashboardDefensorResumoDto } from '@/api.types';
import {
  formatarDataPtBr,
  hrefFolhaHome,
  hrefRelatorioHome,
  rotuloTipoLotacao,
  rotuloTipoPeriodo,
  textoRelatorioHome,
} from '@/modules/home/application/defensor-home.utils';
import '@/modules/system/auth/presentation/pages/escolha-modulo.css';

type DefensorHomeResumoProps = {
  resumo: DashboardDefensorResumoDto | null;
  loading: boolean;
  erro: boolean;
};

export function DefensorHomeSaudacao({ resumo, loading }: Omit<DefensorHomeResumoProps, 'erro'>) {
  const nome = resumo?.nome?.trim();
  return (
    <h2 className="defensor-home-saudacao">
      {loading || !nome ? 'Olá.' : `Olá, ${nome}.`}
    </h2>
  );
}

function ListaOuVazio({
  titulo,
  vazio,
  children,
  possuiItens,
}: {
  titulo: string;
  vazio: string;
  possuiItens: boolean;
  children: ReactNode;
}) {
  return (
    <section className="defensor-home-bloco">
      <h3>{titulo}</h3>
      {possuiItens ? <ul>{children}</ul> : <p className="text-muted mb-0">{vazio}</p>}
    </section>
  );
}

export default function DefensorHomeResumo({ resumo, loading, erro }: DefensorHomeResumoProps) {
  if (loading) {
    return (
      <div className="defensor-home-resumo">
        <p className="text-muted mb-0">Carregando suas informações...</p>
      </div>
    );
  }

  if (erro || !resumo) {
    return (
      <div className="defensor-home-resumo">
        <p className="text-muted mb-0">
          Não foi possível carregar suas informações agora. As áreas do sistema seguem disponíveis acima.
        </p>
      </div>
    );
  }

  return (
    <div className="defensor-home-resumo defensor-home-resumo--colunas">
      <ListaOuVazio
        titulo="Seus vínculos ativos:"
        vazio="Nenhum vínculo vigente no momento."
        possuiItens={resumo.vinculos.length > 0}
      >
        {resumo.vinculos.map((vinculo, index) => (
          <li key={`${vinculo.tipo}-${vinculo.unidade}-${index}`}>
            {rotuloTipoLotacao(vinculo.tipo)} na {vinculo.unidade}
          </li>
        ))}
      </ListaOuVazio>

      <ListaOuVazio
        titulo="Eventos vigentes:"
        vazio="Nenhum evento vigente no momento."
        possuiItens={resumo.eventos.length > 0}
      >
        {resumo.eventos.map((evento, index) => (
          <li key={`${evento.tipo}-${evento.dataInicial}-${index}`}>
            {rotuloTipoPeriodo(evento.tipo)} no período de {formatarDataPtBr(evento.dataInicial)} a{' '}
            {formatarDataPtBr(evento.dataFinal)}.
          </li>
        ))}
      </ListaOuVazio>

      <ListaOuVazio
        titulo="Seus estagiários:"
        vazio="Nenhuma folha aguardando sua assinatura."
        possuiItens={resumo.folhas.length > 0}
      >
        {resumo.folhas.map((folha) => (
          <li key={folha.folhaId}>
            <Link to={hrefFolhaHome(folha.folhaId)}>Folha para assinar de {folha.estagiarioNome}</Link>
          </li>
        ))}
      </ListaOuVazio>

      <ListaOuVazio
        titulo="Seus relatórios:"
        vazio="Nenhum relatório pendente no momento."
        possuiItens={resumo.relatorios.length > 0}
      >
        {resumo.relatorios.map((item, index) => (
          <li key={`${item.mes}-${item.ano}-${item.situacao}-${item.relatorioId ?? index}`}>
            <Link to={hrefRelatorioHome(item)}>{textoRelatorioHome(item)}</Link>
          </li>
        ))}
      </ListaOuVazio>
    </div>
  );
}
